import type { Block } from '@mawkaa/types';
import { HeroBlock } from './blocks/HeroBlock';
import { TextBlock } from './blocks/TextBlock';
import { FeaturesBlock } from './blocks/FeaturesBlock';
import { CTABlock } from './blocks/CTABlock';
import { ContactBlock } from './blocks/ContactBlock';
import { HeaderBlock } from './blocks/HeaderBlock';
import { FooterBlock } from './blocks/FooterBlock';
import { ImageBlock } from './blocks/ImageBlock';
import { ServicesBlock } from './blocks/ServicesBlock';
import { TestimonialsBlock } from './blocks/TestimonialsBlock';
import { FAQBlock } from './blocks/FAQBlock';
import { SpacerBlock } from './blocks/SpacerBlock';
import { DividerBlock } from './blocks/DividerBlock';
import { GalleryBlock } from './blocks/GalleryBlock';
import { VideoBlock } from './blocks/VideoBlock';
import { TeamBlock } from './blocks/TeamBlock';
import { PricingBlock } from './blocks/PricingBlock';
import { MapBlock } from './blocks/MapBlock';
import { MenuBlock } from './blocks/MenuBlock';
import { BookingBlock } from './blocks/BookingBlock';
import { ProductsBlock } from './blocks/ProductsBlock';
import { HoursBlock } from './blocks/HoursBlock';
import { SocialBlock } from './blocks/SocialBlock';
import { NewsletterBlock } from './blocks/NewsletterBlock';
import { HtmlBlock } from './blocks/HtmlBlock';

interface BlockRendererProps {
  block: Block;
  isEditing?: boolean;
}

export function BlockRenderer({ block, isEditing = false }: BlockRendererProps) {
  const { type, content, settings } = block;

  const style: React.CSSProperties = {
    backgroundColor: settings.backgroundColor,
    backgroundImage: settings.backgroundImage ? `url(${settings.backgroundImage})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    paddingTop: settings.paddingTop ? `${settings.paddingTop}px` : undefined,
    paddingBottom: settings.paddingBottom ? `${settings.paddingBottom}px` : undefined,
    marginTop: settings.marginTop ? `${settings.marginTop}px` : undefined,
    marginBottom: settings.marginBottom ? `${settings.marginBottom}px` : undefined,
  };

  const renderBlock = () => {
    switch (type) {
      case 'header':
        return <HeaderBlock content={content} isEditing={isEditing} />;
      case 'hero':
        return <HeroBlock content={content} isEditing={isEditing} />;
      case 'text':
        return <TextBlock content={content} isEditing={isEditing} />;
      case 'image':
        return <ImageBlock content={content} isEditing={isEditing} />;
      case 'gallery':
        return <GalleryBlock content={content} isEditing={isEditing} />;
      case 'video':
        return <VideoBlock content={content} isEditing={isEditing} />;
      case 'features':
        return <FeaturesBlock content={content} isEditing={isEditing} />;
      case 'services':
        return <ServicesBlock content={content} isEditing={isEditing} />;
      case 'testimonials':
        return <TestimonialsBlock content={content} isEditing={isEditing} />;
      case 'team':
        return <TeamBlock content={content} isEditing={isEditing} />;
      case 'pricing':
        return <PricingBlock content={content} isEditing={isEditing} />;
      case 'faq':
        return <FAQBlock content={content} isEditing={isEditing} />;
      case 'contact':
        return <ContactBlock content={content} isEditing={isEditing} />;
      case 'map':
        return <MapBlock content={content} isEditing={isEditing} />;
      case 'cta':
        return <CTABlock content={content} isEditing={isEditing} />;
      case 'footer':
        return <FooterBlock content={content} isEditing={isEditing} />;
      case 'menu':
        return <MenuBlock content={content} isEditing={isEditing} />;
      case 'booking':
        return <BookingBlock content={content} isEditing={isEditing} />;
      case 'products':
        return <ProductsBlock content={content} isEditing={isEditing} />;
      case 'hours':
        return <HoursBlock content={content} isEditing={isEditing} />;
      case 'social':
        return <SocialBlock content={content} isEditing={isEditing} />;
      case 'newsletter':
        return <NewsletterBlock content={content} isEditing={isEditing} />;
      case 'divider':
        return <DividerBlock content={content} />;
      case 'spacer':
        return <SpacerBlock content={content} />;
      case 'html':
        return <HtmlBlock content={content} isEditing={isEditing} />;
      default:
        return (
          <div className="p-8 text-center text-muted-foreground">
            Bloc "{type}" - Aperçu non disponible
          </div>
        );
    }
  };

  if (settings.hidden && !isEditing) {
    return null;
  }

  return (
    <div style={style} className={settings.className}>
      {renderBlock()}
    </div>
  );
}
